import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { appConstants } from "../_constants/app.constants";

/**
 * Loads all the data the poster widgets need (groups + group matches + bracket matches)
 * up-front so the PDF capture never includes "Cargando…" placeholders inside widgets.
 *
 * Returns:
 *   - groups, matches, bracket: arrays (default [])
 *   - loading: true until every request settles
 *   - error: short message if any request failed
 *   - ready: convenience flag = !loading && !error
 *   - reload(): force a re-fetch (e.g. after editing teams/groups elsewhere)
 */
export default function useTournamentPosterData(tournamentId) {
  const [state, setState] = useState({
    groups: [],
    matches: [],
    bracket: [],
    loading: true,
    error: "",
  });
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!tournamentId) {
      setState({ groups: [], matches: [], bracket: [], loading: false, error: "" });
      return undefined;
    }
    let cancelled = false;
    setState((s) => ({ ...s, loading: true, error: "" }));
    (async () => {
      try {
        const [groupsRes, matchesRes, bracketRes] = await Promise.allSettled([
          axios.get(`${appConstants.API_URL}/api/tournamentgroups/${tournamentId}/teams/ordered`),
          axios.get(`${appConstants.API_URL}/api/tournamentmatches/${tournamentId}`),
          axios.get(`${appConstants.API_URL}/api/tournamentmatches/${tournamentId}/bracketmatchs`),
        ]);
        if (cancelled) return;

        const groups = groupsRes.status === "fulfilled" && Array.isArray(groupsRes.value.data) ? groupsRes.value.data : [];
        const matches = matchesRes.status === "fulfilled" && Array.isArray(matchesRes.value.data) ? matchesRes.value.data : [];
        const bracket = bracketRes.status === "fulfilled" && Array.isArray(bracketRes.value.data) ? bracketRes.value.data : [];

        const errors = [];
        if (groupsRes.status === "rejected") errors.push("grupos");
        if (matchesRes.status === "rejected") errors.push("partidos");
        if (bracketRes.status === "rejected") errors.push("bracket");

        matches.sort(
          (a, b) =>
            new Date(a.dayHour ?? a.DayHour ?? 0) - new Date(b.dayHour ?? b.DayHour ?? 0)
        );
        bracket.sort((a, b) => (a.order ?? a.Order ?? 0) - (b.order ?? b.Order ?? 0));

        setState({
          groups,
          matches,
          bracket,
          loading: false,
          error: errors.length === 3 ? "No se pudo cargar el contenido del torneo." : "",
        });
      } catch (e) {
        if (cancelled) return;
        setState({
          groups: [],
          matches: [],
          bracket: [],
          loading: false,
          error: "No se pudo cargar el contenido del torneo.",
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tournamentId, reloadKey]);

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  return { ...state, ready: !state.loading && !state.error, reload };
}
